import React from 'react';
import {
    Box,
    Container,
    Typography,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Card,
    CardContent,
    Skeleton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

import TaskAltIcon from '@mui/icons-material/TaskAlt';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';

const AboutSection = ({data}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const isLoading = data === undefined;

    const stats = [
        { key: 'project_delivered', icon: <EmojiEventsOutlinedIcon /> },
        { key: 'happy_clients', icon: <PeopleAltOutlinedIcon /> },
        { key: 'countries_served', icon: <PublicOutlinedIcon /> },
        { key: 'client_satisfaction', icon: <TaskAltIcon /> },
    ];

    const points =
        data?.meta?.points?.length
        ? data.meta.points
        : t('pages.home.about.points', { returnObjects: true });

    return (
        <Box id="about" sx={{ py: 10, backgroundColor: theme.palette.background.default }}>
            <Container maxWidth="xl">
                {/* MAIN LAYOUT */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 6,
                        alignItems: 'center',
                    }}
                >
                    {/* LEFT SECTION */}
                    <Box sx={{ flex: 1 }}>
                        <Chip
                            label={t('pages.home.about.chip_title')}
                            sx={{
                                backgroundColor: theme.palette.background.chip,
                                color: theme.palette.custom.chipText,
                                fontWeight: 300,
                                fontSize: '1rem',
                                mb: 3,
                                px: 2,
                            }}
                        />

                        <Typography
                            variant="h3"
                            fontWeight="bold"
                            sx={{
                                color: theme.typography.h3.color || '#3784b4ff',
                                mb: 2,
                                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3.5rem' },
                            }}
                        >
                            {isLoading ? (
                                <Skeleton variant="text" height={60} width="80%" />
                            ) : (
                                data?.meta?.title || t('pages.home.about.headline')
                            )}
                        </Typography>

                        <Typography sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }} color="text.secondary">
                            {isLoading ? (
                                <Skeleton variant="text" height={40} width="90%" />
                            ) : (
                                data?.meta?.description_1 || t('pages.home.about.desc_1')
                            )}
                        </Typography>

                        <Typography sx={{ mb: 4, fontSize: '1.1rem', lineHeight: 1.7 }} color="text.secondary">
                            {isLoading ? (
                                <Skeleton variant="text" height={40} width="90%" />
                            ) : (
                                data?.meta?.description_2 || t('pages.home.about.desc_2')
                            )}
                        </Typography>

                        <List disablePadding>
                            {isLoading ? (
                                <PointsSkeleton />
                            ) : (
                                points.map((point, index) => (
                                    <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <TaskAltIcon
                                                sx={{
                                                    color: theme.palette.text.white,
                                                    background: theme.palette.custom.serviceIconBg,
                                                    borderRadius: '50%',
                                                    p: '3px',
                                                }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={point}
                                            primaryTypographyProps={{ fontWeight: 400 }}
                                        />
                                    </ListItem>
                                ))
                            )}
                        </List>
                    </Box>

                    {/* RIGHT SECTION */}
                    <Box sx={{ flex: 1 }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'repeat(2, 1fr)', // mobile → 2 cards
                                    sm: 'repeat(2, 1fr)', // tablet → 2 cards
                                },
                                gap: { xs: 2, md: 4 },
                            }}
                        >
                            {stats.map((stat) => (
                                <Card
                                    key={stat.key}
                                    sx={{
                                        height: '100%',
                                        borderRadius: '20px',
                                        boxShadow: theme.palette.custom.cardShadow,
                                        border: '0.5px solid',
                                        borderColor: 'divider',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            //   transform: 'translateY(-6px)',
                                            boxShadow: theme.palette.custom.cardShadowHover,
                                            borderColor: theme.palette.primary.main,
                                            '& .about-icon': {
                                                transform: 'scale(1.15)',
                                            },
                                        },
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            p: { xs: 2, md: 4 },
                                            textAlign: 'center',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Box
                                            className="about-icon"
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                borderRadius: '14px',
                                                background: theme.palette.custom.serviceIconBg,
                                                color: theme.palette.custom.serviceIconColor,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 1,
                                                transition: 'transform 0.3s ease-in-out',
                                            }}
                                        >
                                            {React.cloneElement(stat.icon, { fontSize: 'large' })}
                                        </Box>

                                        <Typography variant="h4" sx={{ mb: 1, fontWeight: 500, color: "#000000ff" }}>
                                            {isLoading ? (
                                                <Skeleton variant="text" height={20} width={120} />
                                            ) : (
                                                data?.meta?.[stat.key] || t(`pages.home.about.stats.${stat.key}.value`)
                                            )}
                                        </Typography>

                                        <Typography color="text.secondary">
                                            {isLoading ? (
                                                <Skeleton variant="text" height={20} width={120} />
                                            ) : (
                                                t(`pages.home.about.stats.${stat.key}.label`)
                                            )}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

const PointsSkeleton = () => (
    <>
        {[...Array(4)].map((_, i) => (
            <ListItem key={i} disablePadding sx={{ mb: 2 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                    <Skeleton variant="circular" width={26} height={26} />
                </ListItemIcon>
                <ListItemText
                    primary={<Skeleton variant="text" width={`${70 + (i * 5)}%`} height={26} />}
                />
            </ListItem>
        ))}
    </>
);


export default AboutSection;
